/**
 * GuidedTourObjectiveIndex
 *
 * Lists the objectives defined for the current module. Selecting an
 * objective launches the runner with the objective's step sequence.
 * This is the user-facing entry point for the objective-based tour mode
 * (viewMode = 'objective-index').
 */

'use client';

import { Target, CheckCircle, Circle, ArrowLeft, FlaskConical } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export function GuidedTourObjectiveIndex() {
  const { isOpen, moduleKey, engine, objectives, progress, config, openTour } = useGuidedTour();

  if (!isOpen || engine.viewMode !== 'objective-index') {
    return null;
  }

  const verifiedStepIds = progress.progress.verified_step_ids;

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,28rem)]">
      <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
        <CardHeader className="flex flex-row items-start justify-between gap-3 pb-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Target className="h-5 w-5 text-indigo-500" />
              Practice Objectives
            </CardTitle>
            <p className="text-muted-foreground mt-1 text-xs capitalize">
              {config.moduleConfig?.module_name ?? moduleKey}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={engine.returnToIndex}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </CardHeader>
        <CardContent className="max-h-[60vh] space-y-3 overflow-y-auto pt-0">
          {objectives.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-500 dark:text-slate-400">
              No practice objectives are available for this module yet.
            </p>
          )}

          {objectives.map((objective) => {
            const isVerified = objective.verification_step_id
              ? verifiedStepIds.includes(objective.verification_step_id)
              : false;
            const stepCount = objective.step_ids?.length ?? 0;

            return (
              <button
                key={objective.id}
                type="button"
                onClick={() => openTour({ objectiveId: objective.id })}
                className={cn(
                  'flex w-full items-start gap-3 rounded-xl border p-3 text-left transition-colors',
                  'border-slate-100 bg-slate-50/60 hover:bg-slate-100/60',
                  'dark:border-slate-700/80 dark:bg-slate-800/60 dark:hover:bg-slate-700/60'
                )}
              >
                {isVerified ? (
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                ) : (
                  <Circle className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 dark:text-slate-600" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {objective.title}
                    </h3>
                    {objective.requires_sandbox && (
                      <span
                        className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                        title="This objective switches you into the training sandbox — a demo client where nothing affects your real data"
                      >
                        <FlaskConical className="h-2.5 w-2.5" />
                        Sandbox
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{objective.summary}</p>
                  {stepCount > 0 && (
                    <p className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
                      {stepCount} step{stepCount === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
